import type { StorageAccessPolicy, StorageResourceRef, StorageSasKind, StorageSasRequest, StorageService, StorageServiceAvailability } from '../../../../../types/azureStorage';
import { nonMutatingSasLetters, normalizeSasPermissions, sasResourceKindOf, type StorageSasResourceKind } from '../../../../../core/engines/azureStorageSasParameters';

export const SAS_START_SKEW_MS = 15 * 60 * 1000;
export const SAS_DEFAULT_DURATION_MS = 8 * 60 * 60 * 1000;

export type SasFormValue = {
  kind: StorageSasKind; mode: 'ad-hoc' | 'policy'; permissions: string;
  startsOn: string; expiresOn: string; identifier: string; policy?: StorageAccessPolicy;
  services: StorageService[];
  resourceTypes: Array<'service' | 'container' | 'object'>;
};

/** The letter alphabet the target signs with — S7 a share / file its own; S8 a Data Lake directory `directory`, the root a container, a file a blob. */
function requestKind(resource: StorageResourceRef): StorageSasResourceKind {
  const kind = sasResourceKindOf(resource);
  if (kind) return kind;
  throw new Error('This resource cannot receive a SAS.');
}

/** The account SAS services in the SDK's `ss` order, with the names the dialog and the result show. */
export const SAS_SERVICE_NAMES: ReadonlyArray<[StorageService, string]> = [['blob', 'Blob'], ['file', 'File'], ['queue', 'Queue'], ['table', 'Table']];

/**
 * The account SAS's default services (S7 review D F1 — least privilege; the dialog's Services picker shows them before
 * the token exists): ALWAYS one service. File when the dialog was opened from a share or a file; otherwise the first
 * service the account offers in `ss` order — Blob whenever it offers Blob (the behaviour since S3), File on a FileStorage
 * account, and only on an account offering neither Blob nor File the one it has (review E: a Table-only account → Table);
 * Blob while the account's answer is unknown. Any further service joins a token only when the user ticks it.
 */
export function defaultAccountSasServices(available: Partial<Record<StorageService, StorageServiceAvailability>> | undefined, entry: StorageResourceRef): StorageService[] {
  if (entry.kind === 'share' || entry.kind === 'file') return ['file'];
  const offered = (service: StorageService): boolean => available?.[service] !== undefined && available[service]?.state !== 'unavailable';
  return [SAS_SERVICE_NAMES.map(([service]) => service).find(offered) ?? 'blob'];
}
const NAME_OF_LETTER: Record<string, string> = { b: 'Blob', f: 'File', q: 'Queue', t: 'Table' };
/** `ss=bf` → "Blob, File" (the result panel's Services row). */
export const sasServiceNames = (letters: string): string => [...letters].map((letter) => NAME_OF_LETTER[letter] ?? letter).join(', ');

/** `services`: the account SAS's services (`defaultAccountSasServices`); `['blob']` when none are given. */
export function createDefaultSasForm(kind: StorageSasKind, resource: StorageResourceRef, now = new Date(), services: StorageService[] = []): SasFormValue {
  const resourceKind = requestKind(resource);
  return {
    kind, mode: 'ad-hoc', permissions: nonMutatingSasLetters(resourceKind),
    startsOn: new Date(now.getTime() - SAS_START_SKEW_MS).toISOString(),
    expiresOn: new Date(now.getTime() + SAS_DEFAULT_DURATION_MS).toISOString(),
    identifier: '', services: services.length ? [...services] : ['blob'], resourceTypes: ['service', 'container', 'object'],
  };
}

/**
 * A kind chosen for the user (capabilities that arrived late, review E2). Only a service SAS can sign under a stored
 * access policy, so moving to another kind leaves policy mode and refills what the policy governed (permissions, start,
 * expiry emptied by the policy pick) with the defaults; values the user set stay.
 */
export function withAutomaticKind(form: SasFormValue, kind: StorageSasKind, resource: StorageResourceRef): SasFormValue {
  if (form.mode !== 'policy' || kind === 'service') return { ...form, kind };
  return leavePolicyMode({ ...form, kind }, resource);
}

/**
 * Leaving policy mode — the user's Ad-hoc choice or an automatic kind change (fix-round-A review UXA-07): the policy
 * pick is dropped and what it governed (permissions, start, expiry, emptied by the pick) gets editable ad-hoc defaults
 * again; values the user set stay.
 */
export function leavePolicyMode(form: SasFormValue, resource: StorageResourceRef): SasFormValue {
  if (form.mode !== 'policy') return form;
  const defaults = createDefaultSasForm(form.kind, resource);
  return {
    ...form, mode: 'ad-hoc', identifier: '', policy: undefined,
    permissions: form.permissions || defaults.permissions, startsOn: form.startsOn || defaults.startsOn, expiresOn: form.expiresOn || defaults.expiresOn,
  };
}

export function sasFormRequest(connectionId: string, resource: StorageResourceRef, form: SasFormValue): StorageSasRequest {
  const resourceKind = requestKind(resource);
  const normalized = normalizeSasPermissions(resourceKind, form.permissions);
  if (!normalized.ok) throw new Error(normalized.problem.message);
  if (form.mode === 'policy') {
    const policy = form.policy;
    return {
      connectionId, resource, kind: 'service', mode: 'policy', identifier: form.identifier,
      supplement: {
        ...(!policy?.permissions && normalized.value ? { permissions: normalized.value } : {}),
        ...(!policy?.startsOn && form.startsOn ? { startsOn: form.startsOn } : {}),
        ...(!policy?.expiresOn && form.expiresOn ? { expiresOn: form.expiresOn } : {}),
      },
    };
  }
  return {
    connectionId, resource, kind: form.kind, mode: 'ad-hoc', permissions: normalized.value,
    startsOn: form.startsOn, expiresOn: form.expiresOn,
    ...(form.kind === 'account' ? { services: form.services, resourceTypes: form.resourceTypes } : {}),
  };
}
